import { Helmet, HelmetProvider } from "react-helmet-async"
import { Container, Image } from "@chakra-ui/react"

import ogImage from "#/assets/mezo-landing-ogimage.png"
import { SanityCmsDataType, useSanityCmsData } from "#/shared/hooks"
import { buildUrlForSanityCmsImage } from "#/shared/utils"
import {
  HomeFaq,
  HomeFeatures,
  HomeHero,
  HomeMainnet,
  LandingFooter,
  LandingHeader,
  LandingMenu,
  LandingNewsletter,
} from "#/ui/LandingPages"
import { LandingPageData } from "#/ui/LandingPages/landing.types"

export default function LandingPage() {
  const { data, error } = useSanityCmsData<LandingPageData>(
    SanityCmsDataType.Landing,
  )

  const { data: headerData, error: headerError } =
    useSanityCmsData<LandingPageData>(SanityCmsDataType.Header)

  if (!data || error || !headerData || headerError) {
    return null
  }

  const { faq } = data

  const heroImageUrl = data.heroImage?.asset
    ? buildUrlForSanityCmsImage({ asset: data.heroImage.asset })
    : ""

  const socialImageUrl = data.socialImage?.asset
    ? buildUrlForSanityCmsImage({ asset: data.socialImage.asset })
    : ""

  const companyList =
    data?.supernormalCarousel?.map((logo) => ({
      content: (
        <Image
          src={
            logo?.asset ? buildUrlForSanityCmsImage({ asset: logo.asset }) : ""
          }
          alt={logo?.alt || "Default Alt Text"}
          width={200}
          height={200}
        />
      ),
    })) || []

  const featureBlocks =
    data?.features?.map((block) => ({
      title: block.title || "",
      paragraph: block.paragraph || "",
      image: block.image?.asset
        ? buildUrlForSanityCmsImage({ asset: block.image.asset })
        : "",
    })) || []

  return (
    <Container maxW="100%" p={0} pt="100px">
      <HelmetProvider>
        <Helmet>
          <meta
            name="title"
            content={
              data.title ||
              "Mezo is the AppChain for Bitcoiners, unlocking a new economic system where you can borrow, spend, sell, lend, save, and earn interest—all powered by your Bitcoin. Redefine financial freedom with Mezo."
            }
          />
          <meta
            name="description"
            content={
              data.metaDescription ||
              "Mezo, the cutting-edge Layer 2 blockchain enabling Bitcoin's full potential. With BitcoinFi, unlock new ways to engage with Bitcoin on a secure, scalable platform."
            }
          />
          <meta
            property="og:title"
            content={
              data.title ||
              "Mezo: Creating a world where Bitcoin is the norm, not the exception"
            }
          />
          <meta
            property="og:description"
            content={
              data.metaDescription ||
              "Mezo is the AppChain for Bitcoiners, unlocking a new economic system where you can borrow, spend, sell, lend, save, and earn interest—all powered by your Bitcoin. Redefine financial freedom with Mezo."
            }
          />
          <meta
            property="og:image"
            content={socialImageUrl || `https://mezo.org${ogImage}`}
          />
          <meta property="og:url" content="https://mezo.org/" />
          <meta
            name="keywords"
            content={
              data.metaKeywords ||
              "Bitcoin, BitcoinFi, Yield, mezo, mezo.org, btc,"
            }
          />
          <meta name="robots" content="index, follow" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
          <meta name="language" content="English" />
          <title>
            {data.title ||
              "Mezo: Creating a world where Bitcoin is the norm, not the exception"}
          </title>
        </Helmet>
      </HelmetProvider>
      <LandingHeader headerNav={headerData.headerNavNew} />
      <LandingMenu menuNav={headerData.headerNavNew} />
      <HomeHero
        subheader={data.subheader}
        subheader2={data.subheader2}
        heroCTAText={data.heroCTAText}
        heroCTALink={data.heroCTALink}
        heroCTABgColor={data.heroCTABgColor}
        heroCTAFontColor={data.heroCTAFontColor}
        heroImageUrl={heroImageUrl}
      />
      <HomeMainnet
        block1Title={data.block1Title}
        block1Subtitle={data.block1Subtitle}
        block2Title={data.block2Title}
        block2Subtitle={data.block2Subtitle}
        block3Title={data.block3Title}
        block3Subtitle={data.block3Subtitle}
      />
      <HomeFeatures
        feature={featureBlocks}
        featureHeader={data.featureHeader}
      />
      <HomeFaq faq={faq} faqHeader={data.faqHeader} companyList={companyList} />
      <LandingNewsletter
        newsletterHeader="Shake up your inbox"
        newsletterSubheader="Your Bitcoin. Your Future. Your Inbox."
      />
      <LandingFooter footerNav={headerData.footerNavNew} />
    </Container>
  )
}
